import { useRef, useState } from 'react';
import {
    Alert,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Stack,
} from '@mui/material';
import {
    ArrayInput,
    AutocompleteInput,
    Form,
    NumberInput,
    required,
    SimpleFormIterator,
    useGetList,
    usePermissions,
    useSimpleFormIterator,
    useSourceContext
} from 'react-admin';
import { Add } from '@mui/icons-material';
import { useFormContext, useWatch } from 'react-hook-form';
import { AccountRole } from '@football-kids/shared/enums';
import { AppPermissions, getRole } from '@football-kids/client-shared';

type Coach = { id: string; name: string; surname: string };

type InventoryItem = {
    id: string;
    variant: {
        id: string;
        title: string;
        product: { id: string; title: string };
    };
    stock: { stockedQuantity: number };
};

export type SportswearTransferItem = {
    productId: string;
    variantId: string;
    quantity: number;
};

type TransferFormValues = {
    items: SportswearTransferItem[];
    accountId: string;
};

const emptyItem = (): SportswearTransferItem => ({ productId: '', variantId: '', quantity: 1 });
const requiredInput = required();

const areItemsValid = (items: SportswearTransferItem[], inventories: InventoryItem[]) =>
    items.length > 0 && items.every(item => {
        const inventory = inventories.find(
            inventory => inventory.variant.id === item.variantId && inventory.variant.product.id === item.productId
        );
        return inventory && Number.isInteger(item.quantity) && item.quantity > 0
            && item.quantity <= inventory.stock.stockedQuantity;
    }) && new Set(items.map(item => item.variantId)).size === items.length;

const AddItemButton = () => {
    const { add } = useSimpleFormIterator();
    return (
        <Button type="button" startIcon={<Add />} onClick={() => add(emptyItem())}>
            Dodaj przedmiot
        </Button>
    );
};

const TransferItemInputs = ({ inventories, disabled }: {
    inventories: InventoryItem[];
    disabled: boolean;
}) => {
    const { getSource } = useSourceContext();
    const { setValue } = useFormContext<TransferFormValues>();
    const items: SportswearTransferItem[] = useWatch({ name: 'items' }) ?? [];
    const productId = useWatch({ name: getSource('productId') });
    const variantId = useWatch({ name: getSource('variantId') });
    const products = [...new Map(inventories.map(item => [
        item.variant.product.id, item.variant.product,
    ])).values()];
    const variants = [...new Map(inventories.filter(
        inventory => inventory.variant.product.id === productId
            && !items.some(item => item.variantId !== variantId && item.variantId === inventory.variant.id)
    ).map(inventory => [inventory.variant.id, inventory])).values()];
    const selectedVariant = variants.find(inventory => inventory.variant.id === variantId);
    const maxQuantity = selectedVariant?.stock.stockedQuantity;
    const resetQuantity = () => setValue(getSource('quantity') as `items.${number}.quantity`, 1, { shouldDirty: true });

    return (
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ width: '100%', alignItems: 'flex-end' }}>
            <AutocompleteInput
                source="productId"
                label="Produkt"
                choices={products}
                optionText="title"
                validate={requiredInput}
                disabled={disabled}
                onChange={() => {
                    setValue(getSource('variantId') as `items.${number}.variantId`, '', { shouldDirty: true });
                    resetQuantity();
                }}
                sx={{ flex: 1, minWidth: 180 }}
            />
            <AutocompleteInput
                source="variantId"
                label="Wariant"
                choices={variants.map(inventory => ({
                    id: inventory.variant.id,
                    name: `${inventory.variant.title} (Dostępne: ${inventory.stock.stockedQuantity})`,
                }))}
                validate={requiredInput}
                disabled={disabled || !productId}
                onChange={resetQuantity}
                sx={{ flex: 1, minWidth: 200 }}
            />
            <NumberInput
                source="quantity"
                label="Ilość"
                defaultValue={1}
                min={1}
                max={maxQuantity}
                step={1}
                validate={value => !Number.isInteger(value) || value < 1
                    ? 'Podaj dodatnią liczbę całkowitą.'
                    : maxQuantity !== undefined && value > maxQuantity
                        ? `Maksymalna ilość: ${maxQuantity}.`
                        : undefined}
                helperText={maxQuantity !== undefined ? `Maks.: ${maxQuantity}` : undefined}
                disabled={disabled || !selectedVariant}
                sx={{ width: { sm: 110 } }}
            />
        </Stack>
    );
};

const TransferActions = ({ coaches, inventories, disabled, onClose }: {
    coaches: Coach[];
    inventories: InventoryItem[];
    disabled: boolean;
    onClose: () => void;
}) => {
    const { formState: { isSubmitting } } = useFormContext<TransferFormValues>();
    const items = useWatch<TransferFormValues, 'items'>({ name: 'items' }) ?? [];
    const accountId = useWatch({ name: 'accountId' });

    return (
        <DialogActions>
            <Button onClick={onClose} disabled={isSubmitting}>Anuluj</Button>
            <Button type="submit" variant="contained" disabled={disabled || isSubmitting
                || !areItemsValid(items, inventories) || !coaches.some(coach => coach.id === accountId)}>
                {isSubmitting ? 'Zapisywanie…' : 'Przekaż przedmioty'}
            </Button>
        </DialogActions>
    );
};

const SportswearTransferDialog = ({ coaches, onClose, onSubmit }: {
    coaches: Coach[];
    onClose: () => void;
    onSubmit: (items: SportswearTransferItem[], accountId: string) => Promise<void>;
}) => {
    const { permissions } = usePermissions<AppPermissions>();
    const accountRole = permissions ? getRole(permissions) : undefined;
    const [isSubmitting, setIsSubmitting] = useState(false);
    const submittingRef = useRef(false);
    const [error, setError] = useState<string | null>(null);
    const { data: inventories = [], isFetching, error: inventoryError } = useGetList<InventoryItem>(
        "inventory-customer-inventories",
        {
            pagination: { page: 1, perPage: 1000 },
            meta: {
                role: accountRole === AccountRole.ADMIN || accountRole === AccountRole.MANAGER
                    ? 'manager'
                    : 'broker',
            },
        },
        { enabled: !!permissions }
    );
    const availableInventories = inventories.filter(
        item => item.variant?.product && item.stock?.stockedQuantity > 0
    );
    const disabled = !permissions || isSubmitting || isFetching || !!inventoryError;

    const submit = async ({ items, accountId }: TransferFormValues) => {
        if (!permissions || submittingRef.current || !areItemsValid(items, availableInventories) || isFetching || inventoryError
            || !coaches.some(coach => coach.id === accountId)) return;

        submittingRef.current = true;
        setIsSubmitting(true);
        setError(null);
        try {
            await onSubmit(items, accountId);
            onClose();
        } catch {
            setError('Nie udało się przekazać przedmiotów. Spróbuj ponownie.');
        } finally {
            submittingRef.current = false;
            setIsSubmitting(false);
        }
    };

    return (
        <Dialog open onClose={() => { if (!submittingRef.current) onClose(); }} fullWidth maxWidth="md"
            onClick={event => event.stopPropagation()}>
            <DialogTitle>Przedmioty do przekazania</DialogTitle>
            <Form
                record={{}}
                defaultValues={{ items: [emptyItem()], accountId: coaches.length === 1 ? coaches[0].id : '' }}
                onSubmit={submit}
            >
                <DialogContent>
                    {!!inventoryError && <Alert severity="error">Nie udało się pobrać dostępnych przedmiotów.</Alert>}
                    {!!permissions && !isFetching && !inventoryError && availableInventories.length === 0 && (
                        <Alert severity="info">Brak przedmiotów dostępnych do przekazania.</Alert>
                    )}
                    {error && <Alert severity="error">{error}</Alert>}
                    <ArrayInput source="items" label={false}>
                        <SimpleFormIterator
                            disableReordering
                            disableClear
                            disabled={isSubmitting}
                            disableAdd={disabled || availableInventories.length === 0}
                            addButton={<AddItemButton />}
                            sx={{ '& .RaSimpleFormIterator-form': { flex: 1 } }}
                        >
                            <TransferItemInputs inventories={availableInventories} disabled={disabled} />
                        </SimpleFormIterator>
                    </ArrayInput>
                    {coaches.length === 0 && (
                        <Alert severity="warning">Brak trenerów przypisanych do grupy z umowy.</Alert>
                    )}
                    <AutocompleteInput
                        source="accountId"
                        label="Trener"
                        choices={coaches.map(coach => ({ id: coach.id, name: `${coach.name} ${coach.surname}` }))}
                        validate={requiredInput}
                        disabled={isSubmitting}
                    />
                </DialogContent>
                <TransferActions coaches={coaches} inventories={availableInventories} disabled={disabled} onClose={onClose} />
            </Form>
        </Dialog>
    );
};

export default SportswearTransferDialog;
